import {
  keepPreviousData,
  type QueryFunctionContext,
  queryOptions,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { queryKeysDeliveryNote } from '../queryKeys';

import { fetchDeliveryNotePartySuggestions } from './fetchDeliveryNotePartySuggestions';
import {
  type DeliveryNotePartySuggestionsQueryParameters,
  type DeliveryNotePartySuggestionsQueryVariables,
  type DeliveryNotePartySuggestionsResponse,
} from './types';

export const getDeliveryNotePartySuggestionsQueryOptions = <
  TData = DeliveryNotePartySuggestionsResponse,
>({
  params,
  options,
}: {
  params: DeliveryNotePartySuggestionsQueryVariables;
  options?: Omit<
    UseQueryOptions<DeliveryNotePartySuggestionsResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }: QueryFunctionContext) => {
      // `query` may be undefined until there is a name to match. Short-circuit
      // here so both useQuery and useSuspenseQuery consumers get an empty result
      // instead of the fetcher throwing on `undefined.trim()`.
      if (!params.query?.trim()) {
        return { data: [], matchType: null };
      }

      return fetchDeliveryNotePartySuggestions(
        params as DeliveryNotePartySuggestionsQueryParameters,
        { signal },
      );
    },
    queryKey: queryKeysDeliveryNote.getPartySuggestions(params),
    staleTime: ms('30s'),
    ...options,
  });

export const useQueryDeliveryNotePartySuggestions = <
  TData = DeliveryNotePartySuggestionsResponse,
>(
  params: DeliveryNotePartySuggestionsQueryVariables,
  options?: Omit<
    UseQueryOptions<DeliveryNotePartySuggestionsResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery(
    getDeliveryNotePartySuggestionsQueryOptions<TData>({ params, options }),
  );
